import { Can } from "./can"
import { NameTranslations, namesFrom } from "./name-translations"
/**
 * The children you are guardian to, on your own profile.
 *
 * The `guardians` table has been seeded since the fixtures were written and no
 * screen has ever read it. For a youth sports platform that is close to the
 * whole point: a parent in Bangkok signs in and wants to know which team their
 * child is on and where to be on Saturday.
 *
 * Renders nothing when the list is empty *and* the reader has not asked to add
 * anyone. Most people signing in are not guardians, and a permanent "you are
 * not a guardian to anyone" panel on every profile is noise that teaches people
 * to stop reading the page — the same rule the invitations card follows.
 *
 * The Add control is the exception, and it has to be: until 2026-08-31 there
 * was no way to create a player at all, so a real parent signed in to an empty
 * list, saw nothing, and had nowhere to go. A card that hides itself when empty
 * is right; one that hides the only way to stop being empty is a dead end.
 *
 * The relationship is shown — Parent, Grandparent, Legal Guardian — because the
 * model distinguishes them and flattening them to "guardian" would throw away
 * what the table actually says. From the reference vocabulary, so a Thai reader
 * sees ผู้ปกครอง rather than a code.
 */

import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ChevronRightIcon } from "lucide-react"
import { api, orpc } from "../lib/orpc"
import { formErrors } from "../lib/form-errors"
import { useLocale } from "../lib/locale"
import { m } from "../lib/i18n"
import type { Route } from "../lib/router"
import { SectionHeading, SubHeading } from "./page"
import { EmptyState } from "./states"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { DateField } from "./date-field"
import { Item, ItemActions, ItemContent, ItemDescription, ItemGroup, ItemTitle } from "@/components/ui/item"
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select"

/**
 * @answers SIGN_UP_PLAYER_AS_GUARDIAN, EDIT_PLAYER_PROFILE, VIEW_PLAYER
 *
 * A guardian's own children: signing one up, correcting their details, seeing them.
 */
export function YourPlayers({ goto }: { goto: (r: Route) => void }) {
  const { name, label } = useLocale()
  const { data } = useQuery(orpc.players.mine.queryOptions())
  // One at a time. Two open forms on one card is a way to save the wrong child's
  // number, and a guardian is editing one thing.
  const [editing, setEditing] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const players = data?.players ?? []

  return (
    <section className="flex flex-col gap-3">
      <SectionHeading title={m.your_players()} className="mt-0 mb-0">
        {!adding && (
          <Button variant="outline" data-testid="add-player" onClick={() => setAdding(true)}>
            {m.player_add()}
          </Button>
        )}
      </SectionHeading>
      {adding && <AddPlayer onDone={() => setAdding(false)} />}
      {players.length === 0 && !adding && (
        <EmptyState data-testid="your-players-none">{m.your_players_none()}</EmptyState>
      )}
      {players.length > 0 && (
      <ItemGroup data-testid="your-players">
        {players.map((p) =>
          editing === p.playerId ? (
            <Can key={p.playerId} of={p} action="EDIT_PLAYER_PROFILE"><EditPlayer player={p} onDone={() => setEditing(null)} /></Can>
          ) : (
          /**
           * A row, then two controls beside each other — not one inside the
           * other. Interactive content cannot nest: a keyboard user reaches
           * the outer button and an inner one is unreachable, while a screen
           * reader is told about a button that contains a button.
           */
          <Item variant="outline" size="sm" className="p-0" key={p.playerId} data-testid={`your-player-${p.playerId}`}>
            {/*
              The one raw control left in the app, and why it stays one.

              `Item` gives a row its padding and hover, but its hover rule is
              `[a]:hover:bg-muted` — descendant anchors only. This cannot be an
              anchor: a child with no team has nowhere to go, and the row says
              so by being disabled, which is a thing only a button can be. So
              the row is `p-0` and this supplies the padding and hover the
              registry would have supplied to a link.
            */}
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-2.5 px-4 py-3 text-left outline-none hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-default disabled:hover:bg-transparent"
              // Their team, because that is what a guardian is looking for —
              // the squad, the fixtures, who coaches it.
              disabled={!p.teamId}
              data-testid={`goto-team-${p.playerId}`}
              onClick={() => p.teamId && goto({ page: "team", id: p.teamId })}
            >
              <ItemContent>
                <ItemTitle>
                  {name(p.names)}
                  <span className="text-xs font-normal text-muted-foreground tabular-nums">{m.player_jersey({ n: p.jerseyNumber })}</span>
                </ItemTitle>
                <ItemDescription>
                  {[
                    // Null where the player *is* you — being yourself is not a
                    // guardianship, and "Self · Parent" would be nonsense.
                    p.guardianTypeCode ? label("guardianTypes", p.guardianTypeCode) : null,
                    label("positions", p.positionCode),
                    p.teamNames ? name(p.teamNames) : m.player_no_team(),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </ItemDescription>
              </ItemContent>
              {p.teamId && <ChevronRightIcon className="size-4 text-muted-foreground rtl:rotate-180" aria-hidden />}
            </button>
            {/* The model's answer for this reader on this player, not assumed
                from the row being on their own profile — a guardian holds
                EDIT_PLAYER_PROFILE, and so does a coach who is not here. */}
            <Can of={p} action="EDIT_PLAYER_PROFILE">
              <ItemActions className="pr-3">
                <Button
                  variant="link"
                  data-testid={`edit-player-${p.playerId}`}
                  onClick={() => setEditing(p.playerId)}
                >
                  {m.player_edit()}
                </Button>
              </ItemActions>
            </Can>
          </Item>
          ),
        )}
      </ItemGroup>
      )}
    </section>
  )
}

/**
 * Signing up a child.
 *
 * `SIGN_UP_PLAYER_AS_GUARDIAN` is granted to ANY_SIGNED_IN — the model saying
 * any parent may do this, whatever else they are — and it is a different action
 * from `CREATE_PLAYER`, which is a coach adding somebody to the pool. This form
 * is the parent's one, so it writes the guardianship along with the player.
 *
 * The name is a single box, like the team and event forms. It is a locale map
 * underneath, and asking a parent for three languages of their child's name at
 * sign-up would be a worse question than the one it answers.
 *
 * `dob` is asked here and nowhere else. It decides age-group eligibility, so
 * the edit form deliberately cannot change it — which makes this the only
 * moment it can be set, and worth a clear label rather than a bare date box.
 */
function AddPlayer({ onDone }: { onDone: () => void }) {
  const { terms, label } = useLocale()
  const qc = useQueryClient()

  const save = useMutation({
    mutationFn: (v: {
      names: Record<string, string>
      dob: string
      jerseyNumber: number
      positionCode: string
      guardianTypeCode: string
    }) => api.players.signUpAsGuardian(v as never),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orpc.players.key() })
      void qc.invalidateQueries({ queryKey: orpc.teams.key() })
      onDone()
    },
  })

  const err = formErrors(save.error, ["names", "dob", "jerseyNumber", "positionCode"])

  return (
    <Card>
    <CardContent>
    <form
      data-testid="add-player-form"
      onSubmit={(e) => {
        e.preventDefault()
        const f = new FormData(e.currentTarget)
        save.mutate({
          names: namesFrom(f, {}),
          dob: String(f.get("dob")),
          jerseyNumber: Number(f.get("jerseyNumber")),
          positionCode: String(f.get("positionCode")),
          guardianTypeCode: String(f.get("guardianTypeCode")),
        })
      }}
    >
      <FieldGroup className="max-w-[420px]">
        <Field>
          <FieldLabel htmlFor="add-name">{m.player_name()}</FieldLabel>
          <Input id="add-name" name="name" required data-testid="add-player-name" />
        </Field>
        <NameTranslations names={{}} id="add-player-name" />

        <Field>
          <FieldLabel htmlFor="add-dob">{m.player_dob()}</FieldLabel>
          {/* A real date input: the API wants YYYY-MM-DD and a free-text box is how
              "18/04/2012" reaches it and comes back a 400 the parent cannot read.
              The read back underneath is in the parent's language, because the
              native picker is in the browser's. */}
          <DateField id="add-dob" name="dob" required data-testid="add-player-dob" />
        </Field>

        <Field>
          <FieldLabel htmlFor="add-number">{m.player_number()}</FieldLabel>
          <Input
            id="add-number"
            name="jerseyNumber"
            type="number"
            min={0}
            max={99}
            required
            defaultValue={0}
            data-testid="add-player-number"
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="add-position">{m.player_position()}</FieldLabel>
          <NativeSelect id="add-position" name="positionCode" data-testid="add-player-position">
            {terms("positions").map((t) => (
              <NativeSelectOption key={t.code} value={t.code}>{label("positions", t.code)}</NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>

        <Field>
          <FieldLabel htmlFor="add-relationship">{m.player_relationship()}</FieldLabel>
          {/* From the reference vocabulary — Parent, Grandparent, Legal Guardian —
              because the model distinguishes them and the row renders which. */}
          <NativeSelect id="add-relationship" name="guardianTypeCode" data-testid="add-player-relationship">
            {terms("guardianTypes").map((t) => (
              <NativeSelectOption key={t.code} value={t.code}>{label("guardianTypes", t.code)}</NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>

        <div className="flex flex-wrap gap-2">
          <Button type="submit" data-testid="add-player-save" disabled={save.isPending}>
            {save.isPending ? m.event_saving() : m.player_add()}
          </Button>
          <Button type="button" variant="outline" onClick={onDone}>{m.fixture_cancel()}</Button>
        </div>

        {(err.form || err.field("dob")) && (
          <Alert variant="destructive" data-testid="add-player-error">
            <AlertDescription>{err.form ?? err.field("dob")}</AlertDescription>
          </Alert>
        )}
      </FieldGroup>
    </form>
    </CardContent>
    </Card>
  )
}

/**
 * Correcting a squad number or a position.
 *
 * `EDIT_PLAYER_PROFILE` has been granted to SELF, GUARDIAN and the coaches
 * since the fixtures were written, with no procedure and no form. A parent
 * whose child was given the wrong number could do nothing about it.
 *
 * Names use the configured locales; unknown translations survive a save.
 *
 * `dob` is absent because the model has no action for it: it decides age-group
 * eligibility, and a birth date corrected from a profile form makes the
 * eligibility rules advisory.
 */
export function EditPlayer({
  player,
  onDone,
}: {
  player: { playerId: string; names: Record<string, string>; jerseyNumber: number; positionCode: string }
  onDone: () => void
}) {
  const { name, terms, label } = useLocale()
  const qc = useQueryClient()

  const save = useMutation({
    mutationFn: (v: { names: Record<string, string>; jerseyNumber: number; positionCode: string }) =>
      api.players.update({ id: player.playerId, ...v, positionCode: v.positionCode as never }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orpc.players.key() })
      void qc.invalidateQueries({ queryKey: orpc.teams.key() })
      onDone()
    },
  })

  const err = formErrors(save.error, ["jerseyNumber"])

  return (
    <form
      className="flex flex-col gap-4 px-4 py-3"
      data-testid={`player-form-${player.playerId}`}
      onSubmit={(e) => {
        e.preventDefault()
        const f = new FormData(e.currentTarget)
        save.mutate({
          names: namesFrom(f, player.names),
          jerseyNumber: Number(f.get("jerseyNumber")),
          positionCode: String(f.get("positionCode")),
        })
      }}
    >
      <SubHeading>{name(player.names)}</SubHeading>
      <FieldGroup className="max-w-[420px]">
        <Field>
          <FieldLabel htmlFor={`name-${player.playerId}`}>{m.event_name_label()}</FieldLabel>
          <Input id={`name-${player.playerId}`} name="name" defaultValue={player.names.en ?? ""} required />
        </Field>
        <NameTranslations names={player.names} id={`player-name-${player.playerId}`} />

        <Field>
          <FieldLabel htmlFor={`n-${player.playerId}`}>{m.player_number()}</FieldLabel>
          <Input
            id={`n-${player.playerId}`}
            name="jerseyNumber"
            type="number"
            min={0}
            max={99}
            required
            data-testid={`player-number-${player.playerId}`}
            defaultValue={player.jerseyNumber}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor={`p-${player.playerId}`}>{m.player_position()}</FieldLabel>
          <NativeSelect
            id={`p-${player.playerId}`}
            name="positionCode"
            data-testid={`player-position-${player.playerId}`}
            defaultValue={player.positionCode}
          >
            {/* From the reference vocabulary, with the compiled fallback, so the
                control is never an empty box before a fetch lands. */}
            {terms("positions").map((t) => (
              <NativeSelectOption key={t.code} value={t.code}>{label("positions", t.code)}</NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>

        <div className="flex flex-wrap gap-2">
          <Button type="submit" data-testid={`player-save-${player.playerId}`} disabled={save.isPending}>
            {save.isPending ? m.event_saving() : m.event_save()}
          </Button>
          <Button type="button" variant="outline" onClick={onDone}>{m.fixture_cancel()}</Button>
        </div>

        {(err.form || err.field("jerseyNumber")) && (
          <Alert variant="destructive" data-testid={`player-error-${player.playerId}`}>
            <AlertDescription>{err.form ?? err.field("jerseyNumber")}</AlertDescription>
          </Alert>
        )}
      </FieldGroup>
    </form>
  )
}
